import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Col,
  Container,
  Row,
  Card,
  CardBody,
  Input,
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
} from "reactstrap";

import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import BreadCrumb from "Components/Common/BreadCrumb";
import Loader from "Components/Common/Loader";
import { useFormik } from "formik";
import * as Yup from "yup";

import type { RootState } from "store";
import type { ThunkDispatch } from "redux-thunk";
import type { AnyAction } from "redux";
import GoalContainer from "./taskmasterContainer";
import TaskForm from "./taskmasterform";
import { handleValidDate, handleValidTime } from "helpers/common_helper";

import NewDeleteModal from "Components/Common/NewDeleteModal";
import { toast } from "react-toastify";

import {
  fetchTasksThunk,
  addTaskThunk,
  updateTaskThunk,
  deleteTaskThunk,
} from "slices/RBAC/thunk";

import { Link, Navigate, useNavigate } from "react-router-dom";
import { usePermissions } from "../../../hooks/usePermissions";
import { PermissionEnum } from "../../../Components/constants/permissions";

const selectRBACState = (state: RootState) => (state as any).rbac;

const rbacSelector = createSelector(selectRBACState, (rbac) => ({
  tasks: rbac.tasks,
  loading: rbac.loading,
}));

const GoalPage: React.FC = () => {
  const dispatch: ThunkDispatch<RootState, any, AnyAction> = useDispatch();
  const { tasks, loading } = useSelector(rbacSelector);
  const permissions = usePermissions();

  const goalList = tasks || [];

  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editGoalData, setEditGoalData] = useState<any>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedGoalId, setSelectedGoalId] = useState<number | null>(null);

  const loadGoalList = useCallback(() => {
    return dispatch(fetchTasksThunk());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchTasksThunk());
  }, [dispatch]);

  const navigate = useNavigate();

  const handleDeleteClick = (id: number) => {
    setSelectedGoalId(id);
    setDeleteModalOpen(true);
  };

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        id: isEdit
          ? Yup.number().optional()
          : Yup.number()
            .required("Task ID is required")
            .max(9999, "Maximum 4 digits allowed")
            .test(
              "max-digits",
              "Maximum 4 digits allowed",
              function (value) {
                if (!value) return true;
                return value.toString().length <= 4;
              }
            ),
        taskname: Yup.string()
          .trim()
          .required("Task name is required")
          .matches(/^[a-zA-Z/\s_-]+$/, "Task name can only contain Characters")
          .max(70, "Maximum 70 characters allowed"),
        description: Yup.string()
          .trim()
          .required("Description is required")
          .matches(/^[a-zA-Z/\s-]+$/, "Description can only contain Characters")
          .max(100, "Maximum 100 characters allowed"),
        status: isEdit
          ? Yup.number().required("Status is required")
          : Yup.number().optional(),
      }),
    [isEdit]
  );

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: "",
      taskname: "",
      description: "",
      status: 1,
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      // Close modal immediately
      setIsModalOpen(false);
      resetForm();
      const editData = editGoalData;
      const isEditMode = isEdit;
      setIsEdit(false);
      setEditGoalData(null);

      try {
        if (isEditMode && editData) {
          const res = await dispatch(
            updateTaskThunk(editData.taskid, {
              id: editData.taskid,
              taskname: values.taskname,
              description: values.description,
              status: values.status || 1,
            })
          );
          if (!res?.success) {
            toast.error(res?.message || "Failed to update task");
            return;
          }
          toast.success("Task updated successfully!");
        } else {
          const res = await dispatch(
            addTaskThunk({
              id: Number(values.id) || 0,
              taskname: values.taskname,
              description: values.description,
            })
          );
          if (!res?.success) {
            toast.error(res?.message || "Failed to add task");
            return;
          }
          toast.success("Task added successfully!");
        }

        await dispatch(fetchTasksThunk());
      } catch (err) {
        console.error("Error saving task:", err);
        toast.error("An error occurred. Please try again.");
      }
    },
  });

  const toggleModal = () => {
    if (!isModalOpen) {
      validation.resetForm();
      setIsEdit(false);
      setEditGoalData(null);
    }
    setIsModalOpen(!isModalOpen);
  };

  const handleRowClick = useCallback((row: any) => {}, []);

  const handleEditClick = useCallback(
    (row: any) => {
      setIsEdit(true);
      setEditGoalData(row);
      setIsModalOpen(true);

      validation.setValues({
        id: row.taskid || 0,
        taskname: row.taskname || "",
        description: row.description || "",
        status: row.status === "Active" ? 1 : 2,
      });
    },
    [validation]
  );

  const filteredGoals = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return goalList;
    return goalList.filter((t: any) =>
      t.taskname?.toLowerCase().includes(q)
    );
  }, [goalList, searchQuery]);

  const columns = useMemo(
    () => [
      {
        header: "Task ID",
        accessorKey: "taskid",
        cell: ({ row }: any) => {
          const id = row.original.taskid;
          return `#ID${String(id).padStart(3, "0")}`;
        },
      },
      { header: "Task Name", accessorKey: "taskname" },
      { header: "Description", accessorKey: "description" },
      {
        header: "Created On",
        accessorKey: "createdon",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const value = cell.getValue();
          return value ? (
            <>
              {handleValidDate(value)},{" "}
              <small className="text-muted">{handleValidTime(value)}</small>
            </>
          ) : (
            <>N/A</>
          );
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        cell: (cell: any) => {
          const value = cell.getValue();
          const lowerVal = value?.toString().toLowerCase();
          const statusColor =
            lowerVal === "active" ? "#159E3A" : "#A65F00";

          return (
            <span
              style={{
                display: "inline-block",
                padding: "4px 12px",
                borderRadius: "20px",
                fontSize: "12px",
                fontWeight: "500",
                color: statusColor,
                border: `1px solid ${statusColor}`,
                textTransform: "capitalize",
              }}
            >
              {value}
            </span>
          );
        },
      },
      { header: "Created By", accessorKey: "createdby" },
      ...(permissions.includes(PermissionEnum.UPDATE_TASK) || permissions.includes(PermissionEnum.DELETE_TASK) ? [{
        header: "Actions",
        accessorKey: "actions",
        enableColumnFilter: false,
        cell: (cellProps: any) => {
          const row = cellProps.row.original;
  
          return (
            <ul className="list-inline d-flex justify-content-center align-items-center mb-0">
              {permissions.includes(PermissionEnum.UPDATE_TASK) && (
                <li className="list-inline-item" title="Edit">
                  <Button
                    color=""
                    className="btn btn-icon btn-soft-primary btn-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditClick(row);
                    }}
                  >
                    <i className="ri-pencil-fill"></i>
                  </Button>
                </li>
              )}
  
              {/* Delete */}
              {permissions.includes(PermissionEnum.DELETE_TASK) && (
                <li className="list-inline-item" title="Delete">
                  <Button
                    color=""
                    className="btn btn-icon btn-soft-danger btn-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteClick(row.taskid);  
                    }}
                  >
                    <i className="ri-delete-bin-line"></i>
                  </Button>
                </li>
              )}
            </ul>
          );
        },
      }] : []),
    ],
    [handleEditClick, handleDeleteClick, permissions]
  );

  return (
    <div className="page-content">
      <Container fluid className="border pt-4 rounded-4 bg-light mb-3 ms-3 pe-4">
        <BreadCrumb title="Task Master" pageTitle={["RBAC", "Task Master"]} />

        <Row className="align-items-center g-3 d-flex justify-content-between pb-4 pe-3">
          <Col md={3}>
            {/* <div className="position-relative">
              <Input
                type="text"
                className="form-control border ps-5 ms-2 rounded-3"
                value={searchQuery}
                placeholder="Search Category..."
                onChange={(e) => setSearchQuery(e.target.value)}
              /> */}
              {/* <i
                className="ri-search-line position-absolute top-50 start-0 translate-middle-y text-muted ms-3"
                style={{ fontSize: "1.1rem" }}
              />
            </div> */}
          </Col>
          <Col md={9} className="text-end">
          {permissions.includes(PermissionEnum.ADD_TASK) && (
            <Button color="primary" onClick={toggleModal}>
              + Add New
            </Button>
          )}
          </Col>
        </Row>

        <Row>
          <Col lg={12}>
            <Card className="border-0 shadow-none">
              <CardBody>
                {!loading ? (
                  filteredGoals.length > 0 ? (
                    <GoalContainer
                      columns={columns}
                      data={filteredGoals}
                      customPageSize={10000}
                      limit={10000}
                      setPage={() => {}}
                      onRowClick={handleRowClick}
                      tableClass="align-middle border-bottom rounded-pill"
                      theadClass="bg-primary text-white"
                      divClass="table-responsive table-card"
                    />
                  ) : (
                    <div className="text-center p-4">
                      No records found.
                    </div>
                  )
                ) : (
                  <Loader />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Add/Edit Modal */}
      <Modal isOpen={isModalOpen} toggle={toggleModal} centered size="lg">
        <ModalHeader toggle={toggleModal}>
          {isEdit ? "Edit Task" : "Add New Task"}
        </ModalHeader>
        <form onSubmit={validation.handleSubmit}>
          <ModalBody>
            <TaskForm validation={validation} isEdit={isEdit} />
          </ModalBody>
          <ModalFooter>
            {permissions.includes(isEdit ? PermissionEnum.UPDATE_TASK : PermissionEnum.ADD_TASK) && (
              <Button
                type="submit"
                color="primary"
              >
                {isEdit ? "Update" : "Add"}
              </Button>
            )}
          </ModalFooter>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <NewDeleteModal
        show={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Task"
        message="Are you sure you want to delete this task?"
        onConfirm={async () => {
          if (selectedGoalId) {
            setDeleteModalOpen(false);
            const taskid = selectedGoalId;
            setSelectedGoalId(null);
            try {
              const res = await dispatch(deleteTaskThunk(taskid));
              if (!res?.success) {
                toast.error(res?.message || "Failed to delete task");
                return;
              }
              toast.success("Task deleted successfully!");
              await dispatch(fetchTasksThunk());
            } catch (err) {
              console.error("Error deleting task:", err);
              toast.error("An error occurred. Please try again.");
            }
          }
        }}
      />
    </div>
  );
};

export default GoalPage;
